import fedCrimLogo from "@assets/images/logos/fedcrim-small-logo.svg";
import litigAiLogo from "@assets/images/logos/litig-small-icon.svg";
import seriesAiLogo from "@assets/images/logos/JuristAI.svg";
import { siteOrigin } from "@juristai/shared/src/routing/siteOrigin";

const PRACTICE_AREA_DEFINITIONS = [
  {
    appId: "1",
    aliases: ["1"],
    key: "fedcrim",
    name: "Criminal",
    shortName: "Vienna",
    description: "Criminal defense",
    origin: "https://juristai.org",
    dashboardPath: "/dashboard/1",
    accent: "#5abeb5",
    accentStrong: "#0b3b39",
    surface: "#eef8f6",
    logoSrc: fedCrimLogo,
    logoAlt: "Nougat logo",
  },
  {
    appId: "2",
    aliases: ["2"],
    key: "litigai",
    name: "Civil operations",
    shortName: "Vienna",
    description: "Demands and civil litigation",
    origin: "https://juristai.org",
    dashboardPath: "/dashboard/2",
    accent: "#2454d3",
    accentStrong: "#122c75",
    surface: "#eef3ff",
    logoSrc: litigAiLogo,
    logoAlt: "Nougat logo",
  },
  {
    appId: "3",
    productKey: "seriesai",
    aliases: ["3"],
    key: "seriesai",
    name: "Startups & VC (Investor)",
    shortName: "Vienna",
    description: "Startup and venture work",
    origin: "https://juristai.org",
    dashboardPath: "/dashboard/3",
    audience: "investor",
    accent: "#b78521",
    accentStrong: "#5c420d",
    surface: "#fff8e8",
    logoSrc: seriesAiLogo,
    logoAlt: "Nougat logo",
  },
  {
    appId: "4",
    productKey: "seriesai",
    aliases: ["4"],
    key: "seriesai",
    name: "Startups & VC (Founder)",
    shortName: "Vienna",
    description: "Startup and venture work",
    origin: "https://juristai.org",
    dashboardPath: "/dashboard/4",
    audience: "founder",
    accent: "#b78521",
    accentStrong: "#5c420d",
    surface: "#fff8e8",
    logoSrc: seriesAiLogo,
    logoAlt: "Nougat logo",
  },
  {
    appId: "5",
    productKey: "in-house",
    aliases: ["5"],
    key: "in-house",
    name: "In-house",
    shortName: "Vienna",
    description: "Legal requests, contracts, approvals, and obligations",
    origin: "https://juristai.org",
    dashboardPath: "/dashboard/5",
    audience: "in-house-legal-teams",
    accent: "#145c55",
    accentStrong: "#0b3b39",
    surface: "#eef8f6",
    logoSrc: seriesAiLogo,
    logoAlt: "Nougat logo",
  },
];

const PRACTICE_AREA_LOOKUP = PRACTICE_AREA_DEFINITIONS.reduce((lookup, item) => {
  item.aliases.forEach((alias) => {
    lookup[String(alias)] = item;
  });
  return lookup;
}, {});

export const PRACTICE_AREAS = PRACTICE_AREA_DEFINITIONS;
export const PRACTICE_AREA_IDS = PRACTICE_AREA_DEFINITIONS.map((item) => item.appId);

export const normalizePracticeAreaAppId = (appId) => {
  if (appId === undefined || appId === null) {
    return null;
  }

  const normalized = String(appId).trim();
  return PRACTICE_AREA_LOOKUP[normalized]?.appId || normalized || null;
};

export const getPracticeArea = (appId) => {
  const normalized = normalizePracticeAreaAppId(appId);
  return normalized ? PRACTICE_AREA_LOOKUP[normalized] || null : null;
};

export const normalizeAppId = (appId) => {
  if (appId === undefined || appId === null) return null;
  const value = String(appId).trim();
  return PRACTICE_AREA_DEFINITIONS.some((item) => item.appId === value) ? value : value || null;
};

export const getProductKey = (appId) => getPracticeArea(appId)?.productKey || null;

// Every practice area is served by this same site, on whichever domain the
// visitor is on (juristai.org or nougat.law).
export const getPracticeAreaOrigin = (appId) => (getPracticeArea(appId) ? siteOrigin() : null);

export const getPracticeAreaLabel = (appId) =>
  getPracticeArea(appId)?.name || `App ${normalizePracticeAreaAppId(appId) || "unknown"}`;

// `token` is accepted from older callers and ignored: a practice area is the
// same origin as the page linking to it, so the session is already there, and a
// token in the URL would only leak through history, referrers and logs.
// eslint-disable-next-line no-unused-vars
export const buildPracticeAreaUrl = ({ appId, path, token } = {}) => {
  const practiceArea = getPracticeArea(appId);

  if (!practiceArea) {
    return null;
  }

  try {
    const url = new URL(siteOrigin());
    const defaultPath = practiceArea.dashboardPath || "/dashboard";
    const requestedPath = path || defaultPath;
    const normalizedPath = requestedPath.startsWith("/")
      ? requestedPath
      : `/${requestedPath}`;
    const appPathPrefix = `/dashboard/${practiceArea.appId}`;
    const existingDashboardPath = normalizedPath.match(/^\/dashboard\/\d+(\/.*)?$/);
    const logicalDashboardPath = existingDashboardPath
      ? `/dashboard${(existingDashboardPath[1] || "").replace(/^\/\d+/, "")}`
      : normalizedPath;
    const canonicalPath =
      logicalDashboardPath === "/dashboard" || logicalDashboardPath.startsWith("/dashboard/")
        ? logicalDashboardPath === appPathPrefix || logicalDashboardPath.startsWith(`${appPathPrefix}/`)
          ? logicalDashboardPath
          : `${appPathPrefix}${logicalDashboardPath.slice("/dashboard".length)}`
        : logicalDashboardPath;
    url.pathname = canonicalPath;

    return url.toString();
  } catch (error) {
    return null;
  }
};

export const buildPracticeAreaCaseUrl = ({
  appId,
  caseId,
  target = "overview",
  token,
  taskId,
} = {}) => {
  if (!caseId) {
    return null;
  }

  const normalizedAppId = normalizePracticeAreaAppId(appId);
  if (!normalizedAppId || !getPracticeArea(normalizedAppId)) {
    return null;
  }

  const normalizedTarget = String(target || "overview").trim().toLowerCase();
  const encodedCaseId = encodeURIComponent(caseId);
  // Deliberately no case-name slug here: Vienna and Atticus both route
  // slug-less `/dashboard/cases/:id/...` through their own canonical-slug
  // redirect, so building the slug here would risk a stale/mismatched slug
  // and give us two sources of truth for it.
  const basePath = `/dashboard/${normalizedAppId}/cases/${encodedCaseId}`;
  const pathByTarget = {
    overview: `${basePath}/overview`,
    timeline: `${basePath}/timeline`,
    tasks: taskId
      ? `${basePath}/tasks/${encodeURIComponent(taskId)}`
      : `${basePath}/tasks`,
    calendar: `${basePath}/calendar`,
    billing: `${basePath}/billing`,
    signatures: `${basePath}/signatures`,
  };

  return buildPracticeAreaUrl({
    appId: normalizedAppId,
    path: pathByTarget[normalizedTarget] || pathByTarget.overview,
    token,
  });
};
